'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { useAuth } from '@/components/AuthProvider';
import { isProUser, getTrialDaysRemaining, isTrialActive } from '@/lib/auth';
import { useBugReport } from '@/context/BugReportContext';
import TextSizeControl from '@/components/TextSizeControl';

// ── Icons ─────────────────────────────────────────────────────────────────────

function DashboardIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 10L10 3L17 10V17H13V13H7V17H3V10Z" />
    </svg>
  );
}

function PlantIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 18V10" />
      <path d="M10 10C8 10 4 9 4 5C4 2 7 1 9 3C10 4 10 7 10 10Z" />
      <path d="M10 14C12 14 16 13 16 9C16 6 13 5 11 7C10 8 10 11 10 14Z" />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="4" width="16" height="14" rx="2" />
      <path d="M2 8H18" />
      <path d="M6 2V5" />
      <path d="M14 2V5" />
      <rect x="5" y="11" width="2" height="2" rx="0.5" fill="currentColor" stroke="none" />
      <rect x="9" y="11" width="2" height="2" rx="0.5" fill="currentColor" stroke="none" />
      <rect x="13" y="11" width="2" height="2" rx="0.5" fill="currentColor" stroke="none" />
    </svg>
  );
}

function GardenIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M13.5 2.5L17.5 6.5L7 17H3V13L13.5 2.5Z" />
      <path d="M11.5 4.5L15.5 8.5" />
    </svg>
  );
}

function PlannerIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="14" height="12" rx="1.5" />
      <path d="M3 8H17" />
      <path d="M7.5 4V16" />
      <path d="M12.5 4V16" />
      <circle cx="5.5" cy="6" r="0.8" fill="currentColor" stroke="none" />
      <circle cx="10" cy="12" r="0.8" fill="currentColor" stroke="none" />
      <circle cx="14.5" cy="10" r="0.8" fill="currentColor" stroke="none" />
    </svg>
  );
}

function LearnIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 5C4 4 7 4 10 6C13 4 16 4 18 5V16C16 15 13 15 10 17C7 15 4 15 2 16V5Z" />
      <path d="M10 6V17" />
    </svg>
  );
}

function FavoritesIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 17S3 12.5 3 7A4 4 0 0 1 7 3.5L10 6.5L13 3.5A4 4 0 0 1 17 7C17 12.5 10 17 10 17Z" />
    </svg>
  );
}

function TemplatesIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="2" width="7" height="7" rx="1.5" />
      <rect x="11" y="2" width="7" height="7" rx="1.5" />
      <rect x="2" y="11" width="7" height="7" rx="1.5" />
      <rect x="11" y="11" width="7" height="7" rx="1.5" />
    </svg>
  );
}

function UserIcon({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="10" cy="7" r="3.5" />
      <path d="M3 18c0-3.866 3.134-7 7-7s7 3.134 7 7" />
    </svg>
  );
}

function LeafIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M7 13V7" />
      <path d="M7 7C5.5 7 2.5 6 2.5 3C2.5 1.5 4 1 5.5 2.5C6 3.5 6.5 5.5 7 7Z" />
      <path d="M7 7C8.5 7 11.5 6 11.5 3C11.5 1.5 10 1 8.5 2.5C8 3.5 7.5 5.5 7 7Z" />
    </svg>
  );
}

function AskIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 4H17V13H11L7 17V13H3V4Z" />
      <path d="M7 8H13" />
      <path d="M7 10.5H11" />
    </svg>
  );
}

function MoreIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 22 22" fill="currentColor">
      <circle cx="5" cy="11" r="1.5" />
      <circle cx="11" cy="11" r="1.5" />
      <circle cx="17" cy="11" r="1.5" />
    </svg>
  );
}

function AlmanacIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 4.5A1.5 1.5 0 0 1 4.5 3H8a2 2 0 0 1 2 2v11a1.5 1.5 0 0 0-1.5-1.5h-4A1.5 1.5 0 0 1 3 13V4.5Z" />
      <path d="M17 4.5A1.5 1.5 0 0 0 15.5 3H12a2 2 0 0 0-2 2v11a1.5 1.5 0 0 1 1.5-1.5h4A1.5 1.5 0 0 0 17 13V4.5Z" />
      <path d="M10 16v2" />
    </svg>
  );
}

function CalculatorIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="2" width="14" height="16" rx="2" />
      <rect x="6" y="5" width="8" height="3" rx="1" />
      <circle cx="6.5" cy="11" r="0.75" fill="currentColor" stroke="none" />
      <circle cx="10" cy="11" r="0.75" fill="currentColor" stroke="none" />
      <circle cx="13.5" cy="11" r="0.75" fill="currentColor" stroke="none" />
      <circle cx="6.5" cy="14" r="0.75" fill="currentColor" stroke="none" />
      <circle cx="10" cy="14" r="0.75" fill="currentColor" stroke="none" />
      <circle cx="13.5" cy="14" r="0.75" fill="currentColor" stroke="none" />
    </svg>
  );
}

function AppleIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11.25 4.25C11.55 2.95 12.45 1.95 13.75 1.5" />
      <path d="M10.1 5.2C8.85 3.8 6.6 3.65 5.2 5C3.45 6.65 3.25 9.4 4.45 12.1C5.65 14.8 7.4 17 10 17C12.6 17 14.35 14.8 15.55 12.1C16.75 9.4 16.55 6.65 14.8 5C13.4 3.65 11.35 3.8 10.1 5.2Z" />
      <path d="M10 5.15C9.35 4.1 8.05 3.25 6.6 3.25" />
      <path d="M9.8 4.95C8.7 4.55 7.8 3.8 7.35 2.75" />
    </svg>
  );
}

function SeedTrayIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 8.5H17L15.75 16H4.25L3 8.5Z" />
      <path d="M6.5 8.5V16" />
      <path d="M10 8.5V16" />
      <path d="M13.5 8.5V16" />
      <path d="M7 7V4.75" />
      <path d="M7 4.75C6.2 4.75 5 4.25 5 2.9C5 2.2 5.7 2 6.35 2.55C6.75 2.95 6.95 3.85 7 4.75Z" />
      <path d="M7 4.75C7.8 4.75 9 4.25 9 2.9C9 2.2 8.3 2 7.65 2.55C7.25 2.95 7.05 3.85 7 4.75Z" />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 3L10 8L6 13" />
    </svg>
  );
}

function BugIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 15V8" />
      <path d="M7 8C7 6.34 8.34 5 10 5C11.66 5 13 6.34 13 8V13C13 14.66 11.66 16 10 16C8.34 16 7 14.66 7 13V8Z" />
      <path d="M7 9H4M13 9H16M7 12H4M13 12H16" />
      <path d="M8 5L6 3M12 5L14 3" />
    </svg>
  );
}

function PestIdentifyIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="8.5" cy="8.5" r="5.5" />
      <path d="M13.5 13.5L17.5 17.5" />
      <path d="M8.5 6V9.5M7 7.5h3" />
    </svg>
  );
}

function BeneficialIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <ellipse cx="10" cy="13" rx="4" ry="5" />
      <path d="M10 8V5" />
      <path d="M10 5C8.5 5 6 4 5 2M10 5C11.5 5 14 4 15 2" />
      <path d="M6.5 10.5L3 9M13.5 10.5L17 9" />
    </svg>
  );
}

function RemediesIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="6" y="8" width="8" height="10" rx="1.5" />
      <path d="M7.5 8V5.5A2.5 2.5 0 0 1 12.5 5.5V8" />
      <path d="M9 13h2M10 12v2" />
    </svg>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

// ── Nav data ──────────────────────────────────────────────────────────────────

const navGroups = [
  {
    label: 'Core',
    items: [
      { label: 'Dashboard',      mobileLabel: 'Dashboard', href: '/dashboard',                          Icon: DashboardIcon },
      { label: 'My Garden',      mobileLabel: 'Garden',    href: '/garden',                             Icon: GardenIcon    },
      { label: 'Garden Planner', mobileLabel: 'Planner',   href: '/garden?tab=planner',                 Icon: PlannerIcon   },
      { label: 'Fruit Tree Planner', mobileLabel: 'Fruit', href: '/tools/fruit-tree-planner', Icon: AppleIcon    },
    ],
  },
  {
    label: 'Discovery',
    items: [
      { label: 'Plant Library', mobileLabel: 'Plants',    href: '/plants',    Icon: PlantIcon     },
      { label: 'Favorites',     mobileLabel: 'Favorites', href: '/favorites', Icon: FavoritesIcon },
      { label: 'Templates',     mobileLabel: 'Templates', href: '/templates', Icon: TemplatesIcon },
    ],
  },
  {
    label: 'Tools',
    items: [
      { label: 'Calendar',                  mobileLabel: 'Calendar', href: '/calendar',                  Icon: CalendarIcon   },
      { label: 'Seed Starting Calculator',  mobileLabel: 'Seeds',    href: '/seed-starting-calculator',  Icon: SeedTrayIcon   },
      { label: 'Soil Calculator',           mobileLabel: 'Calc',     href: '/tools/soil-calculator',     Icon: CalculatorIcon },
      { label: 'Almanac',                   mobileLabel: 'Almanac',  href: '/tools/almanac',             Icon: AlmanacIcon    },
    ],
  },
  {
    label: 'Learning',
    items: [
      { label: 'Learn',                    mobileLabel: 'Learn',    href: '/learn',        Icon: LearnIcon        },
      { label: 'Identify Pest or Disease', mobileLabel: 'Identify',   href: '/learn/pests',      Icon: PestIdentifyIcon },
      { label: 'Garden Allies',             mobileLabel: 'Allies',     href: '/learn/beneficial', Icon: BeneficialIcon   },
      { label: 'Garden Remedies',          mobileLabel: 'Remedies',   href: '/learn/remedies',   Icon: RemediesIcon     },
      { label: 'Ask Garden',               mobileLabel: 'Ask',        href: '/ask',              Icon: AskIcon          },
    ],
  },
];

const navItems = navGroups.flatMap(g => g.items);

// Mobile keeps the same order as desktop: first five in the bottom bar,
// remaining items in the More sheet.
const mobileNavItems = navItems.slice(0, 5);
const moreSheetItems = navItems.slice(5);

function isNavItemActive(href: string, pathname: string, gardenTabParam: string | null): boolean {
  const isPlannerHref = href === '/garden?tab=planner';
  const isGardenHref = href === '/garden';

  if (isPlannerHref) return pathname === '/garden' && gardenTabParam === 'planner';
  if (isGardenHref) return pathname === '/garden' && gardenTabParam !== 'planner';

  // Exact match for /learn so it doesn't highlight on /learn/pests or other sub-pages
  if (href === '/learn') return pathname === '/learn';

  return pathname.startsWith(href);
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function Navigation() {
  const pathname = usePathname();
  const router   = useRouter();
  const { user, loading, logout } = useAuth();
  const { openBugReport } = useBugReport();
  const isPro      = user ? isProUser(user) : false;
  const daysLeft   = user ? getTrialDaysRemaining(user) : 0;
  const isTrialing = user ? isTrialActive(user) : false;

  // useState hooks must come before any early return (Rules of Hooks)
  const [showAccountSheet,   setShowAccountSheet]   = useState(false);
  const [showMoreSheet,      setShowMoreSheet]      = useState(false);
  const [gardenTabParam,     setGardenTabParam]     = useState<string | null>(null);
  const [showTextSizePanel,  setShowTextSizePanel]  = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    function syncGardenTab() {
      setGardenTabParam(new URLSearchParams(window.location.search).get('tab'));
    }

    syncGardenTab();
    window.addEventListener('popstate', syncGardenTab);
    return () => window.removeEventListener('popstate', syncGardenTab);
  }, [pathname]);

  // Hide on full-page routes that manage their own layout
  if (
    pathname === '/' ||
    pathname === '/onboarding' ||
    pathname === '/philosophy' ||
    pathname === '/disclaimer' ||
	    pathname === '/admin/community-plants' ||
	    pathname === '/admin/community-pests' ||
	    pathname === '/admin/community-photos' ||
    pathname === '/admin/pro-overrides'
  ) return null;

  // Highlight More when the active route lives inside the More sheet.
  const moreIsActive = moreSheetItems.some(item => isNavItemActive(item.href, pathname, gardenTabParam));

  function handleMobileLogout() {
    logout();
    setShowAccountSheet(false);
    router.push('/dashboard');
  }

  function openMore() {
    setShowAccountSheet(false);
    setShowMoreSheet(v => !v);
  }

  function openAccount() {
    setShowMoreSheet(false);
    setShowAccountSheet(true);
  }

  function handleNavIntent(href: string) {
    if (href === '/garden?tab=planner') setGardenTabParam('planner');
    if (href === '/garden') setGardenTabParam(null);
  }

  return (
    <>
      {/* ── Desktop Sidebar ── */}
      <aside className="hidden md:flex flex-col fixed inset-y-0 left-0 w-[260px] bg-white border-r border-sand z-40">

        {/* Logo */}
        <Link
          href="/dashboard"
          className="px-6 pt-5 pb-4 flex items-center gap-3 hover:opacity-80 transition-opacity"
        >
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src="/images/logo/Willowbottom-Logo.png" alt="" className="h-11 w-auto flex-shrink-0" />
          <div>
            <div className="font-heading font-bold text-[22px] leading-none text-deep-green">Garden</div>
            <div className="font-body italic text-[12px] text-sage mt-0.5">by Willowbottom</div>
          </div>
        </Link>

        {/* Nav links */}
        <nav className="flex-1 overflow-y-auto px-3 pb-2">
          {navGroups.map((group) => (
            <div key={group.label} className="mb-1">
              <p className="px-4 pt-3 pb-0.5 font-body text-[10px] font-bold tracking-widest uppercase text-dark-text/50 select-none">
                {group.label}
              </p>
              {group.items.map(({ label, href, Icon }) => {
                const isActive = isNavItemActive(href, pathname, gardenTabParam);
                return (
                  <Link
                    key={href}
                    href={href}
                    onClick={() => handleNavIntent(href)}
                    className={[
                      'flex items-center gap-3 px-4 py-[8px] rounded-lg text-[14px] font-body border-l-[3px] transition-all duration-200',
                      isActive
                        ? 'bg-light-sage text-deep-green border-deep-green'
                        : 'text-dark-text border-transparent hover:bg-[#f0f5ee]',
                    ].join(' ')}
                  >
                    <Icon />
                    {label}
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>

        {/* Text size panel — toggled by the Aa button below */}
        {showTextSizePanel && (
          <div className="flex-shrink-0 border-t border-sand">
            <div className="flex items-center justify-between px-4 pt-2.5 pb-0">
              <span className="font-body text-[10px] font-bold tracking-widest uppercase text-dark-text/50 select-none">Text Size</span>
              <button
                onClick={() => setShowTextSizePanel(false)}
                className="text-sage hover:text-dark-text transition-colors p-0.5"
                aria-label="Close text size panel"
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                  <path d="M3 3l8 8M11 3l-8 8" />
                </svg>
              </button>
            </div>
            <TextSizeControl />
          </div>
        )}

        {/* Philosophy + Disclaimer + Privacy + Terms links — one line */}
        <div className="flex-shrink-0 px-4 pt-1 pb-0 flex items-center gap-x-1.5 flex-wrap">
          <Link href="/philosophy" className="font-body text-[10px] text-sage hover:text-deep-green transition-colors whitespace-nowrap">Our Philosophy</Link>
          <span className="font-body text-[10px] text-sand leading-none">·</span>
          <Link href="/disclaimer" className="font-body text-[10px] text-sage hover:text-deep-green transition-colors whitespace-nowrap">Disclaimer</Link>
          <span className="font-body text-[10px] text-sand leading-none">·</span>
          <Link href="/privacy" className="font-body text-[10px] text-sage hover:text-deep-green transition-colors whitespace-nowrap">Privacy</Link>
          <span className="font-body text-[10px] text-sand leading-none">·</span>
          <Link href="/terms" className="font-body text-[10px] text-sage hover:text-deep-green transition-colors whitespace-nowrap">Terms</Link>
        </div>

        {/* Report an issue + Text size toggle */}
        <div className="flex-shrink-0 px-4 pt-0.5 pb-1.5 flex items-center justify-between">
          <button
            onClick={openBugReport}
            className="font-body text-[10px] text-sage hover:text-dark-text transition-colors"
          >
            Report an issue
          </button>
          <button
            onClick={() => setShowTextSizePanel(v => !v)}
            aria-label="Text size"
            aria-expanded={showTextSizePanel}
            className={[
              'font-heading font-bold text-[13px] px-1.5 py-0.5 rounded transition-colors',
              showTextSizePanel
                ? 'text-deep-green bg-light-sage'
                : 'text-sage hover:text-dark-text',
            ].join(' ')}
          >
            Aa
          </button>
        </div>

        {/* User section — always visible, pinned to bottom */}
        <div className="flex-shrink-0 border-t border-sand pt-2.5 pb-3 px-3">
          {!loading && user ? (
            <>
              {isTrialing ? (
                <div className="flex items-center justify-between bg-light-sage border border-[#C5D9B8] rounded-lg px-3 py-2 mb-2">
                  <span className="font-body text-[12px] text-sage">
                    Trial: <strong className="text-deep-green">{daysLeft}d left</strong>
                  </span>
                  <Link href="/pricing" className="font-body text-[11px] font-semibold text-deep-green hover:underline">
                    View plans
                  </Link>
                </div>
              ) : !isPro ? (
                <div className="bg-[#F4F8F2] border border-[#D4E6CC] rounded-xl px-3.5 py-3 mb-2">
                  <p className="font-body text-[13px] font-semibold text-dark-text mb-0.5">Upgrade to Pro</p>
                  <p className="font-body text-[11px] text-sage mb-2.5 leading-snug">Unlock advanced planning tools</p>
                  <Link
                    href="/pricing"
                    className="block w-full bg-deep-green text-white font-body font-bold text-[13px] py-2 rounded-lg text-center hover:opacity-90 transition-opacity"
                  >
                    Upgrade
                  </Link>
                </div>
              ) : null}

              {/* Avatar + name + email row */}
              <Link
                href="/account"
                className="flex items-center gap-2.5 px-2 py-2 rounded-lg hover:bg-[#f0f5ee] transition-colors cursor-pointer"
              >
                <div
                  className="flex-shrink-0 flex items-center justify-center rounded-full bg-deep-green text-white font-body font-semibold"
                  style={{ width: 36, height: 36, fontSize: 14 }}
                >
                  {getInitials(user.name)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <p className="m-0 font-body text-[13px] font-semibold text-dark-text truncate">
                      {user.name}
                    </p>
                    {isPro && (
                      <span className="font-body text-[10px] font-bold text-white bg-deep-green px-1.5 py-0.5 rounded-full leading-none flex-shrink-0">
                        Pro
                      </span>
                    )}
                  </div>
                  <p className="m-0 font-body text-[11px] text-sage truncate">{user.email}</p>
                </div>
              </Link>

            </>
          ) : !loading ? (
            <Link
              href="/login"
              className="block w-full bg-deep-green text-white font-body font-bold text-[14px] py-2.5 rounded-lg text-center transition-all duration-200 hover:opacity-90"
            >
              Sign in
            </Link>
          ) : null}
        </div>
      </aside>

      {/* ── Mobile Header ── */}
      <header className="md:hidden fixed top-0 left-0 right-0 h-12 bg-white border-b border-sand flex items-center px-4 z-40">
        <Link href="/dashboard" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src="/images/logo/Willowbottom-Logo.png" alt="" className="h-8 w-auto flex-shrink-0" />
          <div>
            <div className="font-heading font-bold text-[18px] leading-none text-deep-green">Garden</div>
            <div className="font-body italic text-[10px] text-sage leading-none mt-0.5">by Willowbottom</div>
          </div>
        </Link>
      </header>

      {/* ── Mobile Bottom Nav ── */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 h-[60px] bg-white border-t border-sand z-40">
        <div className="flex h-full">

          {/* Primary nav items */}
          {mobileNavItems.map(({ label, href, Icon }) => {
            const isActive = isNavItemActive(href, pathname, gardenTabParam);
            return (
              <Link
                key={href}
                href={href}
                onClick={() => handleNavIntent(href)}
                className={[
                  'flex-1 flex flex-col items-center justify-center gap-0.5 px-0.5 text-center text-[9px] leading-tight font-body font-medium transition-colors duration-200',
                  isActive ? 'text-deep-green' : 'text-sage',
                ].join(' ')}
              >
                <span className="[&_svg]:w-[20px] [&_svg]:h-[20px]"><Icon /></span>
                <span>{label}</span>
              </Link>
            );
          })}

          {/* More - toggles the rest of the desktop-ordered navigation */}
          <button
            onClick={openMore}
            className={[
              'flex-1 flex flex-col items-center justify-center gap-0.5 text-[10px] font-body font-medium transition-colors duration-200',
              moreIsActive || showMoreSheet ? 'text-deep-green' : 'text-sage',
            ].join(' ')}
          >
            <MoreIcon />
            <span>More</span>
          </button>

          {/* Account / Sign In */}
          {!loading && (
            user ? (
              <button
                onClick={openAccount}
                className={[
                  'flex-1 flex flex-col items-center justify-center gap-0.5 text-[10px] font-body font-medium transition-colors duration-200',
                  pathname.startsWith('/account') ? 'text-deep-green' : 'text-sage',
                ].join(' ')}
              >
                <span className="[&_svg]:w-[22px] [&_svg]:h-[22px]"><UserIcon /></span>
                <span>Account</span>
              </button>
            ) : (
              <Link
                href="/login"
                className="flex-1 flex flex-col items-center justify-center gap-0.5 text-[10px] font-body font-medium text-sage transition-colors duration-200"
              >
                <span className="[&_svg]:w-[22px] [&_svg]:h-[22px]"><UserIcon /></span>
                <span>Sign In</span>
              </Link>
            )
          )}
        </div>
      </nav>

      {/* ── More Bottom Sheet ── */}
      <div
        className={[
          'md:hidden fixed inset-0 bg-black/40 z-50 transition-opacity duration-200',
          showMoreSheet ? 'opacity-100' : 'opacity-0 pointer-events-none',
        ].join(' ')}
        onClick={() => setShowMoreSheet(false)}
      />
      <div
        className={[
          'md:hidden fixed bottom-0 left-0 right-0 bg-white rounded-t-2xl z-[51] transition-transform duration-300 ease-out',
          showMoreSheet ? 'translate-y-0' : 'translate-y-full',
        ].join(' ')}
      >
        {/* Drag handle */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 rounded-full bg-[#d1d5db]" />
        </div>

        {/* Sheet title */}
        <p className="font-body text-[14px] text-sage text-center py-2">More</p>

        {/* Rows */}
        <div>
          {moreSheetItems.map(({ label, href, Icon }, index) => {
            const isActive = isNavItemActive(href, pathname, gardenTabParam);
            return (
              <div key={href}>
                {index > 0 && <div className="border-t border-sand mx-5" />}
                <Link
                  href={href}
                  onClick={() => {
                    handleNavIntent(href);
                    setShowMoreSheet(false);
                  }}
                  className={[
                    'flex items-center h-[52px] px-5 gap-4 hover:bg-[#f0f5ee] active:bg-[#f0f5ee] transition-colors',
                    isActive ? 'bg-light-sage text-deep-green' : 'text-dark-text',
                  ].join(' ')}
                >
                  <span className="text-deep-green flex-shrink-0"><Icon /></span>
                  <span className="flex-1 font-body text-[15px]">{label}</span>
                  <span className="text-sage flex-shrink-0"><ChevronRightIcon /></span>
                </Link>
              </div>
            );
          })}
          <div className="border-t border-sand mx-5" />
          <button
            onClick={() => { setShowMoreSheet(false); openBugReport(); }}
            className="flex items-center h-[52px] px-5 gap-4 w-full hover:bg-[#f0f5ee] active:bg-[#f0f5ee] transition-colors"
          >
            <span className="text-sage flex-shrink-0"><BugIcon /></span>
            <span className="flex-1 font-body text-[15px] text-dark-text text-left">Report an Issue</span>
          </button>
        </div>

        {/* Safe-area bottom padding */}
        <div style={{ paddingBottom: 'env(safe-area-inset-bottom, 16px)' }} className="pb-4" />
      </div>

      {/* ── Floating Ask Garden button (mobile, not on /ask page) ── */}
      {!loading && user && !pathname.startsWith('/ask') && (
        <Link
          href="/ask"
          className="md:hidden fixed z-40 flex items-center justify-center w-[52px] h-[52px] rounded-full bg-deep-green text-white shadow-lg hover:opacity-90 transition-opacity"
          style={{ bottom: '76px', right: '16px' }}
          aria-label="Ask Garden"
        >
          <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 5H18V14H12L8 18V14H4V5Z" />
            <path d="M8 9H14" />
            <path d="M8 11.5H12" />
          </svg>
        </Link>
      )}

      {/* ── Account Bottom Sheet ── */}
      <div
        className={[
          'md:hidden fixed inset-0 bg-black/40 z-50 transition-opacity duration-200',
          showAccountSheet ? 'opacity-100' : 'opacity-0 pointer-events-none',
        ].join(' ')}
        onClick={() => setShowAccountSheet(false)}
      />
      <div
        className={[
          'md:hidden fixed bottom-0 left-0 right-0 bg-white rounded-t-2xl z-[51] transition-transform duration-300 ease-out',
          showAccountSheet ? 'translate-y-0' : 'translate-y-full',
        ].join(' ')}
      >
        {/* User info */}
        <div className="px-5 pt-6 pb-4">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-light-sage border border-[#C5D9B8] flex items-center justify-center text-deep-green flex-shrink-0">
              <UserIcon size={22} />
            </div>
            <div className="min-w-0">
              <div className="font-body text-[15px] font-semibold text-dark-text truncate">
                {user?.name}
              </div>
              <div className="font-body text-[13px] text-sage truncate">
                {user?.email}
              </div>
            </div>
          </div>

          {user?.zone && (
            <div className="mt-3 flex items-center gap-1.5 text-deep-green">
              <LeafIcon />
              <span className="font-body text-[13px] text-sage">Zone {user.zone}</span>
            </div>
          )}
        </div>

        <div className="border-t border-sand" />

        {/* Menu items */}
        <div className="px-2 py-2">
          {/* Upgrade row — shown for free and trial users, hidden for paid Pro */}
          {user && !isPro && (
            <Link
              href="/pricing"
              onClick={() => setShowAccountSheet(false)}
              className="flex items-center gap-3 px-4 py-3.5 rounded-lg hover:bg-[#f0f5ee] active:bg-[#f0f5ee] transition-colors w-full"
            >
              <span className="flex-1 font-body text-[15px] text-warm-brown font-semibold">
                {isTrialing ? 'View plans' : 'Start free trial'}
              </span>
              {isTrialing ? (
                <span className="font-body text-[11px] font-semibold text-sage bg-light-sage
                                 border border-[#C5D9B8] px-2 py-0.5 rounded-full">
                  Trial active
                </span>
              ) : (
                <LeafIcon />
              )}
            </Link>
          )}
          <Link
            href="/account"
            onClick={() => setShowAccountSheet(false)}
            className="flex items-center px-4 py-3.5 rounded-lg font-body text-[15px] text-dark-text hover:bg-[#f0f5ee] active:bg-[#f0f5ee] transition-colors w-full"
          >
            Account Settings
          </Link>
          <button
            onClick={handleMobileLogout}
            className="flex items-center px-4 py-3.5 rounded-lg font-body text-[15px] text-alert-red hover:bg-[#fff5f5] active:bg-[#fff5f5] transition-colors w-full text-left"
          >
            Sign Out
          </button>
        </div>

        {/* Text size */}
        <div className="border-t border-sand">
          <TextSizeControl />
        </div>

        {/* Cancel pill */}
        <div className="px-5 pt-1 pb-8">
          <button
            onClick={() => setShowAccountSheet(false)}
            className="w-full font-body text-[14px] font-medium text-sage bg-[#f5f5f0] rounded-full py-3 text-center transition-colors hover:bg-[#eeede8]"
          >
            Cancel
          </button>
        </div>
      </div>
    </>
  );
}
